<template>
    <table-view
        ref="tableView"
        :actions="actions"
        :breadcrumbs="breadcrumbs"
        :figure="figure"
        :listeners="listeners"
        :resource-manager="resourceManager"
        :title="title"
    >
        <template #actions>
            <div class="slds-col slds-no-flex slds-grid slds-align-top slds-p-bottom_xx-small">
                <slds-button
                    ref="newButton"
                    data-cy="new-group-button"
                    icon="utility:add"
                    :label="$t('label.new').toString()"
                    neutral
                    @click="handlers.clickNew"
                />
            </div>
        </template>
    </table-view>
</template>

<script>
import Figure from '@/core/components/common/figure'
import Breadcrumb from '@/core/components/common/breadcrumb'
import TableView from '@/core/components/table-view/table-view'
import TableAction from '@/core/components/common/table-action'
import {EVENTS, ICONS, LABELS, PLURALIZATION, RESOURCES, ROUTE_NAMES} from '@/constants'
import {MODALS} from '@/component-definitions'

export default {
    name: 'GroupsView',

    components: {
        TableView
    },

    data() {
        return {
            /**
             * Data table available actions.
             *
             * @type {TableAction[]}
             */
            actions: [
                new TableAction(this.$t(LABELS.EDIT), EVENTS.EDIT, ICONS.UTILITY.EDIT),
                new TableAction(this.$t(LABELS.DELETE), EVENTS.DELETE, ICONS.UTILITY.DELETE),
            ],

            /**
             * Page header breadcrumbs.
             *
             * @type {Breadcrumb[]}
             */
            breadcrumbs: [
                new Breadcrumb(RESOURCES.GROUPS.NAME, this.$tc(LABELS.GROUP, PLURALIZATION.PLURAL))
            ],

            /**
             * Page header figure.
             *
             * @type {Figure}
             */
            figure: new Figure(RESOURCES.GROUPS.FIGURE.ICON, RESOURCES.GROUPS.FIGURE.COLOR),

            /**
             * Data table action listeners.
             *
             * @type {Object}
             */
            listeners: null,

            /**
             * Group resource manager.
             *
             * @type {GroupManager}
             */
            resourceManager: this.$resources.groups,

            /**
             * Page header title.
             *
             * @type {string}
             */
            title: this.$tc(LABELS.GROUP, PLURALIZATION.PLURAL),
        }
    },

    created() {
        this.initializeListeners()
    },

    handlers: {
        /**
         * Event handler for clicking new button.
         */
        clickNew() {
            this.openNewGroupModal()
        },

        /**
         * Event handler for clicking on row details.
         *
         * @param {GroupTableRow} row - Clicked row.
         * @return {Promise}
         */
        async details(row) {
            await this.navigateToGroupDetailsAsync(row.id)
        },

        /**
         * Event handler for clicking on row owner.
         *
         * @param {GroupTableRow} row - Clicked row.
         * @return {Promise}
         */
        async owner(row) {
            await this.navigateToGroupOwnerAsync(row.ownerId)
        },
    },

    methods: {
        /**
         * Initializes the listeners for this table view.
         */
        initializeListeners() {
            this.listeners = {
                details: this.handlers.details,
                owner: this.handlers.owner,
            }
        },

        /**
         * Navigate to Group details view.
         *
         * @param {string} id - Group record ID.
         * @return {Promise}
         */
        async navigateToGroupDetailsAsync(id) {
            await this.$router.push({
                name: ROUTE_NAMES.GROUP_DETAILS,
                params: {id},
            })
        },

        /**
         * Navigate to Group owner view.
         *
         * @param {string} id - Group owner record ID.
         * @return {Promise}
         */
        async navigateToGroupOwnerAsync(id) {
            await this.$router.push({
                name: ROUTE_NAMES.PERSON_VIEW,
                params: {id},
            })
        },

        /**
         * Opens the New Group modal.
         */
        openNewGroupModal() {
            this.$dialogs.openModal(MODALS.GROUPS.NEW)
        },
    }
}
</script>
